import { Product, ProductValueUnit } from '@dh-io-emh/search-result-store';
import { TFunction } from 'i18next';

export const getEmissionType = (emissionData: any) => {
  const wltp = emissionData.uidata?.attributes || [];

  if (wltp.length) return 'wltp';

  const nefz = emissionData.uidata?.overlay?.attributes || [];

  if (nefz.length) return 'nefz';

  return null;
};

export const getEmissionData = (product: Product): unknown[] => {
  if (product && product.localEmissionAndConsumptionData) {
    try {
      // parse emission data json string and look for specified keys in either NEFZ or WLTP object
      const emissionData = JSON.parse(product.localEmissionAndConsumptionData);

      if (getEmissionType(emissionData) === 'wltp') return emissionData.uidata?.attributes;

      if (getEmissionType(emissionData) === 'nefz') return emissionData.uidata?.overlay?.attributes;
    } catch (e) {
      return [];
    }
  }

  return [];
};

export const getEmissionFootnotes = (product: Product): Record<string, unknown> | undefined | null => {
  if (product && product.localEmissionAndConsumptionData) {
    try {
      // parse emission data json string and look for either NEFZ or WLTP footnotes
      const emissionData = JSON.parse(product.localEmissionAndConsumptionData);

      if (getEmissionType(emissionData) === 'wltp') return emissionData.uidata?.footnotes;

      if (getEmissionType(emissionData) === 'nefz') return emissionData.uidata?.overlay?.footnotes;
    } catch (e) {
      return null;
    }
  }

  return null;
};

export const getEngineDescription = (product: Product, t: TFunction): string => {
  // Not initialized, return empty description
  const valueAndUnit = (power: ProductValueUnit): string => {
    return `${power.value} ${power.unit}`;
  };

  // Pointers to decrease verbose code on switch block
  const combustionMain = product.combustionPower ?? {};
  const combustionSecondary = product.combustionPowerSecondary ?? {};
  const electricMain = product.electricPower ?? {};
  const electricSecondary = product.electricPowerSecondary ?? {};

  const getFullValue = (primaryValue: string, secondaryValue?: string) => {
    if (!secondaryValue) return `${primaryValue}`;

    return `${primaryValue} (${secondaryValue})`;
  };

  const getCombinedValue = (
    combustion: ProductValueUnit,
    electric: ProductValueUnit,
    powerLabels?: boolean,
  ): string => {
    // Imported translation function from Vue instance as composables cannot be used here.

    const ELECTRIC_KEY = 'dcp.powerElectricLabel';
    const HYBRID_KEY = 'dcp.powerHybridLabel';

    if (combustion.value) {
      const powerHybridValue: string = t(HYBRID_KEY, {
        power: valueAndUnit(electric),
      });

      if (powerLabels && powerHybridValue !== HYBRID_KEY) {
        return `${valueAndUnit(combustion)}${electric?.value ? ` + ${powerHybridValue}` : ''}`;
      } else {
        return `${valueAndUnit(combustion)}${electric?.value ? ` + ${valueAndUnit(electric)}` : ''}`;
      }
    }

    // Hybrid cars. Use both combustion and electric if present.
    if (electric.value) {
      const powerElectricValue: string = t(ELECTRIC_KEY, {
        power: valueAndUnit(electric),
      });

      if (powerLabels && powerElectricValue !== ELECTRIC_KEY) {
        return `${powerElectricValue} ${combustion?.value ? ` + ${valueAndUnit(combustion)}` : ''}`;
      } else {
        return `${valueAndUnit(electric)} ${combustion?.value ? ` + ${valueAndUnit(combustion)}` : ''}`;
      }
    }

    return '';
  };

  const primaryValue = getCombinedValue(combustionMain, electricMain, true);
  const secondaryValue = getCombinedValue(combustionSecondary, electricSecondary);

  // Use secondaryValue as primary as a fallback solution
  return primaryValue ? getFullValue(primaryValue, secondaryValue) : getFullValue(secondaryValue, '');
};
